import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/joy';
import { pdfjs, Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfPreviewProps {
    file: File | null;
    onFieldClick?: (fieldName: string) => void;
    activeField?: string | null;
    mapping?: Record<string, string>;
}

export default function PdfPreview({ file, onFieldClick, activeField, mapping }: PdfPreviewProps) {
    const [numPages, setNumPages] = useState<number>(0);
    const [fileUrl, setFileUrl] = useState<string | null>(null);

    useEffect(() => {
        if (file) {
            const url = URL.createObjectURL(file);
            setFileUrl(url);
            return () => URL.revokeObjectURL(url);
        } else {
            setFileUrl(null);
        }
    }, [file]);

    const [annotationLayerRendered, setAnnotationLayerRendered] = useState<number>(0);

    const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    };

    const handleAnnotationLayerSuccess = () => {
        setAnnotationLayerRendered(prev => prev + 1);
    };

    // Attach global click listener to intercept clicks on PDF annotations
    useEffect(() => {
        const handleAnnotationClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            // Intercept clicks on the label we inject, or the input itself
            const isLabel = target.classList.contains('pdf-field-label');
            const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
            
            if ((isInput || isLabel) && target.closest('.annotationLayer')) {
                // If it's the label, the input is a sibling
                const input = isLabel ? target.previousElementSibling as HTMLInputElement : target as HTMLInputElement;
                if (input && input.name && onFieldClick) {
                    onFieldClick(input.name);
                }
            }
        };

        document.addEventListener('click', handleAnnotationClick);
        return () => document.removeEventListener('click', handleAnnotationClick);
    }, [onFieldClick]);

    // Effect to render labels and green "mapped" styles over the inputs
    useEffect(() => {
        const inputs = document.querySelectorAll('.annotationLayer input, .annotationLayer textarea, .annotationLayer select');
        inputs.forEach((input: any) => {
            const name = input.name;
            if (!name) return;

            let label = input.parentElement.querySelector('.pdf-field-label') as HTMLElement;
            if (!label) {
                label = document.createElement('div');
                label.className = 'pdf-field-label';
                label.style.position = 'absolute';
                label.style.top = '50%';
                label.style.left = '50%';
                label.style.transform = 'translate(-50%, -50%)';
                label.style.cursor = 'pointer';
                label.style.fontSize = '10px';
                label.style.fontWeight = 'bold';
                label.style.whiteSpace = 'nowrap';
                label.style.overflow = 'hidden';
                label.style.textOverflow = 'ellipsis';
                label.style.maxWidth = '90%';
                label.style.zIndex = '2';
                // Center text
                label.style.textAlign = 'center';
                input.parentElement.appendChild(label);
            }

            const mappedValue = mapping?.[name];
            
            if (mappedValue) {
                label.innerText = mappedValue;
                label.style.color = '#166534'; // Dark green
                input.classList.add('pdf-field-mapped');
            } else {
                label.innerText = name;
                label.style.color = '#1e3a8a'; // Dark blue
                input.classList.remove('pdf-field-mapped');
            }
        });
    }, [mapping, numPages, fileUrl, annotationLayerRendered]); // Include annotationLayerRendered

    // Effect to highlight and scroll to active field in the PDF
    useEffect(() => {
        // Remove active class from all inputs
        document.querySelectorAll('.pdf-field-active').forEach(el => {
            el.classList.remove('pdf-field-active');
        });

        if (activeField) {
            // Find the input with the exact name (safely escaping quotes if any)
            const safeFieldName = activeField.replace(/"/g, '\\"');
            const input = document.querySelector(`.annotationLayer input[name="${safeFieldName}"], .annotationLayer textarea[name="${safeFieldName}"], .annotationLayer select[name="${safeFieldName}"]`);
            
            if (input) {
                input.classList.add('pdf-field-active');
                
                // Scroll the PDF viewer container so the field is visible
                input.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
            }
        }
    }, [activeField, annotationLayerRendered]);

    if (!fileUrl) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%" bgcolor="background.surface" minHeight={400}>
                <Typography color="neutral">No PDF selected for preview.</Typography>
            </Box>
        );
    }

    return (
        <Box 
            sx={{ 
                height: '100%',
                flex: 1,
                overflow: 'auto', 
                bgcolor: 'background.level1',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                '& .annotationLayer input, & .annotationLayer textarea, & .annotationLayer select': {
                    cursor: 'pointer !important',
                    border: 'none !important',
                    backgroundColor: 'rgba(59, 130, 246, 0.15) !important',
                    borderRadius: '2px !important',
                    transition: 'all 0.2s',
                    color: 'transparent !important', 
                    boxShadow: 'none !important',
                },
                '& .annotationLayer input:hover, & .annotationLayer textarea:hover, & .annotationLayer select:hover': {
                    backgroundColor: 'rgba(59, 130, 246, 0.25) !important',
                    zIndex: 5,
                },
                '& .annotationLayer input.pdf-field-mapped, & .annotationLayer textarea.pdf-field-mapped, & .annotationLayer select.pdf-field-mapped': {
                    border: 'none !important',
                    backgroundColor: 'rgba(34, 197, 94, 0.15) !important', // Soft Green
                },
                '& .annotationLayer input.pdf-field-mapped:hover, & .annotationLayer textarea.pdf-field-mapped:hover, & .annotationLayer select.pdf-field-mapped:hover': {
                    backgroundColor: 'rgba(34, 197, 94, 0.25) !important',
                },
                '& .annotationLayer input.pdf-field-active, & .annotationLayer textarea.pdf-field-active, & .annotationLayer select.pdf-field-active': {
                    border: '2px solid #0284c7 !important', // Crisp blue border for active
                    backgroundColor: 'rgba(2, 132, 199, 0.15) !important',
                    boxShadow: 'none !important',
                    zIndex: 10,
                }
            }}
        >
            <Document
                file={fileUrl}
                onLoadSuccess={handleDocumentLoadSuccess}
                loading={<CircularProgress />}
                error={<Typography color="danger">Failed to load PDF.</Typography>}
            >
                {Array.from(new Array(numPages), (el, index) => (
                    <Box key={`page_${index + 1}`} mb={2} sx={{ boxShadow: 'md', bgcolor: 'white' }}>
                        <Page 
                            pageNumber={index + 1} 
                            renderTextLayer={true}
                            renderAnnotationLayer={true}
                            onRenderAnnotationLayerSuccess={handleAnnotationLayerSuccess}
                            renderForms={true}
                            width={600}
                        />
                    </Box>
                ))}
            </Document>
        </Box>
    );
}
